import { useEffect, useRef, useState } from "react";
import { format } from "date-fns";
import { ArrowLeft, Database, FilePlus, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useWorkspacePermissions } from "@/hooks/use-workspace-permissions";
import { isUnfinished, useKbFiles, useKbStores, useKbUsage } from "@/hooks/use-knowledge-base";
import type { KnowledgeBaseFile } from "@/services/knowledge-base.service";
import { filesTowardLimit } from "@/utils/kb-upload-rules";
import { DescriptionPanel } from "./DescriptionPanel";
import { FilesTable } from "./FilesTable";
import { KbTestSearch } from "./KbTestSearch";
import { TextDialog } from "./TextDialog";
import { UploadDialog } from "./UploadDialog";
import { UsageStrip } from "./UsageStrip";

/**
 * The description is rewritten in the background a few seconds after the last
 * file finishes, or after a delete (design §4): for this long the knowledge
 * base is read again every few seconds, so the new one shows without a reload.
 */
export const DESCRIPTION_WAIT_MS = 30_000;

interface KnowledgeBaseDetailProps {
  workspaceId: string;
  id: string;
  onBack: () => void;
}

/** One knowledge base: the plan's room, its description, its files, the upload, text added by hand, and the test
 * console with the default rule. */
export function KnowledgeBaseDetail({ workspaceId, id, onBack }: KnowledgeBaseDetailProps) {
  const { canEdit, canDelete } = useWorkspacePermissions();
  const [pollUntil, setPollUntil] = useState(0);
  const stores = useKbStores(workspaceId, pollUntil);
  const usage = useKbUsage(workspaceId, pollUntil);
  const kb = stores.data?.find((store) => store.id === id);
  const files = useKbFiles(kb?.id);
  /** The upload dialog while it is open: for new files, or for one that replaces `replacing`. */
  const [upload, setUpload] = useState<{ replacing?: KnowledgeBaseFile } | null>(null);
  /** Percent sent of each file this browser is uploading, by file id; null once its upload has settled. */
  const [progress, setProgress] = useState<Record<string, number | null>>({});
  /** The text dialog while it is open: for new text, or for the text file it edits. */
  const [text, setText] = useState<{ editing?: KnowledgeBaseFile } | null>(null);

  const watchDescription = () => setPollUntil(Date.now() + DESCRIPTION_WAIT_MS);
  const busy = files.data?.some(isUnfinished) ?? false;
  const wasBusy = useRef(busy);
  useEffect(() => {
    if (wasBusy.current && !busy) setPollUntil(Date.now() + DESCRIPTION_WAIT_MS);
    wasBusy.current = busy;
  }, [busy]);

  const back = (
    <Button variant="ghost" className="mb-4 pl-0 hover:bg-transparent hover:text-primary" onClick={onBack}>
      <ArrowLeft className="mr-2 h-4 w-4" />
      Back to knowledge bases
    </Button>
  );

  if (stores.isPending) {
    return (
      <div className="p-8" role="status" aria-label="Loading the knowledge base">
        <Skeleton className="mb-4 h-8 w-64" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }
  if (!kb) {
    return (
      <div className="p-8">
        {back}
        <p role="alert" className="text-muted-foreground">
          {stores.isError ? "The knowledge base could not be loaded." : "This knowledge base no longer exists."}
        </p>
      </div>
    );
  }

  return (
    <div className="h-full space-y-6 overflow-y-auto p-8">
      <div>
        {back}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-primary/10 p-3">
              <Database className="h-8 w-8 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">{kb.display_name}</h1>
              <p className="text-sm text-muted-foreground">Created {format(new Date(kb.created_at), "MMM d, yyyy")}</p>
            </div>
          </div>
          {canEdit && (
            <div className="flex flex-wrap justify-end gap-2">
              <KbTestSearch workspaceId={workspaceId} storeIds={[kb.id]} />
              <Button variant="outline" onClick={() => setText({})} disabled={!files.data}>
                <FilePlus className="mr-2 h-4 w-4" />
                Add text
              </Button>
              <Button onClick={() => setUpload({})} disabled={!files.data}>
                <Upload className="mr-2 h-4 w-4" />
                Upload files
              </Button>
            </div>
          )}
        </div>
      </div>

      {usage.data && files.data && <UsageStrip usage={usage.data} files={filesTowardLimit(files.data)} />}

      <DescriptionPanel kb={kb} files={files.data ?? []} workspaceId={workspaceId} canEdit={canEdit} />

      <section aria-label="Files" className="space-y-3">
        <h2 className="text-lg font-semibold">Files</h2>
        {files.isPending ? (
          <Skeleton role="status" aria-label="Loading files" className="h-32 w-full" />
        ) : files.isError ? (
          <div role="alert" className="flex items-center gap-4 rounded-lg border border-dashed p-6">
            <p className="text-muted-foreground">The files could not be loaded.</p>
            <Button variant="outline" onClick={() => void files.refetch()}>
              Try again
            </Button>
          </div>
        ) : (
          <FilesTable
            storeId={kb.id}
            workspaceId={workspaceId}
            files={files.data}
            progress={progress}
            canEdit={canEdit}
            canDelete={canDelete}
            maxFiles={usage.data?.limits.max_files_per_kb}
            onUpload={() => setUpload({})}
            onReplace={(file) => setUpload({ replacing: file })}
            onEdit={(file) => setText({ editing: file })}
            onDeleted={watchDescription}
          />
        )}
      </section>

      {upload && (
        <UploadDialog
          storeId={kb.id}
          workspaceId={workspaceId}
          files={files.data}
          usage={usage.data}
          replacing={upload.replacing}
          onClose={() => setUpload(null)}
          onProgress={(fileId, percent) => setProgress((sent) => ({ ...sent, [fileId]: percent }))}
        />
      )}

      {text && (
        <TextDialog
          storeId={kb.id}
          workspaceId={workspaceId}
          files={files.data}
          usage={usage.data}
          editing={text.editing}
          onClose={() => setText(null)}
        />
      )}
    </div>
  );
}
